import { useEffect, useState } from "react";
import { BookOpenCheck, FileText, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, DOMAINES, domaineLabel, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MARQUES = ["loxone", "unv", "unifi", "reyee", "daikin", "heiwa", "schneider", "legrand", "autre"];
const LABELS = { loxone: "Loxone", unv: "UNV / Uniview", unifi: "Ubiquiti UniFi", reyee: "Reyee", daikin: "Daikin", heiwa: "Heiwa", schneider: "Schneider", legrand: "Legrand", autre: "Autre" };

export default function AdminNotices() {
  const [items, setItems] = useState([]);
  const [marque, setMarque] = useState("loxone");
  const [domaine, setDomaine] = useState("");
  const [titre, setTitre] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/notices").then(({ data }) => setItems(data));

  useEffect(() => {
    load();
  }, []);

  const envoyer = async () => {
    if (!file) return toast.error("Choisissez un PDF");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("marque", marque);
    fd.append("domaine", domaine);
    fd.append("titre", titre);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notices", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Notice indexée : ${data.pages} pages, ${data.extraits} extraits`);
      setFile(null);
      setTitre("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Import impossible"));
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (n) => {
    await api.delete(`/admin/notices/${n.id}`);
    toast.success("Notice retirée de la base IA");
    load();
  };

  const totalExtraits = items.reduce((s, n) => s + (n.extraits || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-notices">
      <div>
        <div className="bk-label mb-2">Base de connaissances IA</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Notices constructeurs</h1>
        <p className="text-sm text-slate-600 mt-2">
          Déposez les notices PDF (Loxone, UNV, UniFi, Reyee, Daikin, Heiwa…). Elles sont découpées page par page et
          interrogées par l'assistant, qui cite ensuite la notice et la page dans ses réponses.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
          <div className="bk-label">Importer une notice (PDF, 60 Mo max)</div>
          <select
            data-testid="notice-marque-select"
            value={marque}
            onChange={(e) => setMarque(e.target.value)}
            className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            {MARQUES.map((m) => (
              <option key={m} value={m}>{LABELS[m]}</option>
            ))}
          </select>
          <select
            data-testid="notice-domaine-select"
            value={domaine}
            onChange={(e) => setDomaine(e.target.value)}
            className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            <option value="">Domaine (optionnel)</option>
            {DOMAINES.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
          <Input
            data-testid="notice-titre-input"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            placeholder="Titre affiché (ex : Manuel Miniserver Gen 2)"
            className="h-11"
          />
          <input
            data-testid="notice-file-input"
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-600"
          />
          <Button data-testid="notice-upload-btn" onClick={envoyer} disabled={busy} className="bg-[#005CFF] hover:bg-[#0047CC]">
            <Upload size={15} className="mr-2" /> {busy ? "Indexation en cours…" : "Importer et indexer"}
          </Button>
          <p className="text-xs text-slate-400">
            Les PDF scannés (images sans texte) sont refusés : l'IA ne peut pas les lire. Demandez la version texte au constructeur.
          </p>
        </div>

        <div className="space-y-2">
          <div className="bk-label flex items-center gap-2" data-testid="notices-stats">
            <BookOpenCheck size={13} /> {items.length} notice(s) · {totalExtraits} extraits indexés
          </div>
          {items.map((n) => (
            <div key={n.id} data-testid={`notice-row-${n.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                  <FileText size={14} className="text-slate-400 shrink-0" /> {n.titre}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {LABELS[n.marque] || n.marque}
                  {n.domaine ? ` · ${domaineLabel(n.domaine)}` : ""} · {n.pages} pages · {n.extraits} extraits · {Math.round((n.size || 0) / 1024)} Ko · {fmtDate(n.created_at)}
                </div>
              </div>
              <button data-testid={`notice-delete-${n.id}`} onClick={() => supprimer(n)} className="text-slate-300 hover:text-red-500 shrink-0">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="notices-empty">
              Aucune notice importée : l'assistant s'appuie pour l'instant sur les tutoriels et la fiche constructeur interne.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
